import { ShipmentCardData } from '../ShipmentCard';
// src/pages/Shipments/Filters/types.ts
import type { Permissions } from '../Filters/FilterDropdownMenus';
import { ReactNode, ReactElement } from 'react';
import {
  AIStateKey,
  RawAIStateSection,
} from '@/pages/AiHistory/components/AIStateFilter';
import {
  SentimentKey,
  RawSentimentSection,
} from '@/pages/AiHistory/components/SentimentFilter';
import type { CommunicationTypeKey } from '@/pages/AiHistory/components/TypeFilter';
import type { AlertSection } from '../Filters/CustomAlertThresholdFilter';
import type { FeaturePermissionMatrix } from '@/apis/types';

import { CheckboxChangeEvent } from 'antd';

import type { MenuProps } from 'antd';
import { legTypes, type ShipmentAlertActivitySummary } from '@/apis/shipment';

export type FilterMenuItem = Required<MenuProps>['items'][number];
export type FilterMenuItems = FilterMenuItem[];

export type ID = string | number;

export interface ListItem {
  id: string;
  enabled?: boolean;
  name?: string;
  title?: string;
}

export interface DateBucketItem {
  period?: string;
  start?: string;
  end?: string;
}

export type NonDateFilterValue = string[] | number[];
export type AlertAssignmentSelection =
  | { type: 'unassigned' }
  | { type: 'role'; key: string }
  | { type: 'user' | 'team'; id: string };

// what the backend expects in payload
export interface ShipmentFiltersPayload {
  type?: number[];
  leg_type?: 'CL' | 'EL';
  [key: string]:
    | 'CL'
    | 'EL'
    | NonDateFilterValue
    | AlertAssignmentSelection[]
    | boolean
    | { values: string[]; logic: 'AND' | 'OR' }
    | Record<number, DateBucketItem>
    | undefined;
}

export type HandleApplyFiltersArgs = {
  filterBy?: string;
  items?: ListItem[];
  append?: boolean;
  groupTitle?: string;
};

export interface AIStateCountsPayload {
  search?: string;
  date_range?: string;

  custom_range?: {
    start: string;
    end: string;
    period?: string;
  } | null;

  [key: string]: unknown;
}

export interface ShipmentsFilterPanelProps {
  appliedSearchQuery?: string;
  setAppliedSearchQuery?: (v: string) => void;

  aiStateSections?: RawAIStateSection[];
  savedFilterKey: string;
  showAiStateFilter?: boolean;
  aiStateSelected?: AIStateKey[];
  setAIStateSelected?: (val: AIStateKey[]) => void;

  sentimentSections?: RawSentimentSection[];
  sentimentSelected?: SentimentKey[];
  setSentimentSelected?: (v: SentimentKey[]) => void;
  showSentimentFilter?: boolean;

  typeSelected?: CommunicationTypeKey[];
  setTypeSelected?: (v: CommunicationTypeKey[]) => void;

  token: string;
  permissions?: Permissions;
  appliedFilters: AppliedFilter[];
  setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFilter[]>>;

  filterArray: FilterArrayItem[];
  setFilterArray: (items: FilterArrayItem[]) => void;

  setFiltersPayload: (payload: ShipmentFiltersPayload | null) => void;

  searchQuery: string;
  setSearchQuery: (val: string) => void;
  isSearchVisible: boolean;
  setIsSearchVisible: (val: boolean) => void;

  page: number;
  pageSize: number;

  onRunSearch: (params: {
    query: string;
    check: boolean;
    page?: number;
    pageSize?: number;
    calledFrom?: string;
    payload?: ShipmentFiltersPayload | null;
  }) => void;

  // so parent can call internal handleApplyFilters
  registerHandleApplyFilters?: (
    fn: (args: HandleApplyFiltersArgs) => void
  ) => void;
  didRunInitialFetchRef?: React.MutableRefObject<boolean>;
}

export type FilterItem = {
  filterBy: string;
  filterValue: string[];
};

export interface MenuPermission {
  is_create?: boolean;
  is_view?: boolean;
  is_update?: boolean;
  [key: string]: unknown;
}

export interface MenuItem {
  name?: string;
  permissions?: MenuPermission;
  sub_menus?: MenuItem[];
}

export interface RootState {
  menu: {
    items: MenuItem[];
    features?: MenuItem[];
  };
}

export type Shipment = ShipmentCardData;

export type ShipmentStatus = string | number | boolean | null;

export interface SummaryData {
  is_critical?: number;
  is_risk?: number;
  is_track?: number;
  [key: string]: unknown;
}

export interface DrawerStateFromHome {
  dState: string;
  shipmentId: string | null;
  cocId?: string | null;
}

export interface DrawerState {
  isOpen: boolean;
  name: string;
  shipmentId: string | null;
  alert: unknown;
  legType?: legTypes;
}

export type ToggleDrawerFn = (
  state: 'open' | 'close',
  name: string,
  shipmentId: string | null,
  alert?: unknown,
  legType?: 'CL' | 'EL'
) => void;

export interface SettingsPermissions {
  locationsView: boolean;
  transportProvidersView: boolean;
  productsView: boolean;
}

export interface ShipmentRequestBody {
  page: number;
  page_size: number;
  search: string;
  [key: string]: unknown;
}

export interface ShipmentsDataInner {
  is_success?: boolean;
  message?: string;
  data?: Shipment[];
  page_count?: number;
  shipment_count?: number;
  total_shipment_count?: number | null;
  over_all_shipment_count?: number | null;
  active_shipment_count?: number;
  count_pending?: boolean;
}

export interface ShipmentsApiResponse {
  data?: {
    data?: ShipmentsDataInner;
  };
}

export interface SummaryApiInner {
  is_success?: boolean;
  message?: string;
  data?:
    | (SummaryData & {
        version?: number;
        unchanged?: boolean;
      })
    | null;
}

export interface ShipmentsVersionInner {
  changed?: boolean;
  version?: number;
}

export interface ShipmentsVersionApiResponse {
  data?: {
    data?: ShipmentsVersionInner;
  };
}

export interface FiltersState {
  payload: Record<string, unknown> | null;
}

export interface SubmenuAnchor {
  left: number;
  top: number;
  el?: HTMLElement | null;
}

export interface FilterDropdownsProps {
  addFilterBgColor?: string;
  addFilterFontSize?: string;
  openedFromPill: boolean;
  dropdownMaxHeight?: number | string;
  submenuAnchor?: SubmenuAnchor;
  appliedFilters?: AppliedFilter[];
  menu: ReactElement;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  secondFilterDisplay: boolean;
  setSecondFilterDisplay: (display: boolean) => void;
  filterBy: string;
  isDatePicker: boolean;
  calendarMenu: (options: {
    hideBackArrow: boolean;
    maxHeight?: number;
  }) => ReactNode;
  renderSubMenu: (options: {
    hideBackArrow: boolean;
    maxHeight?: number;
  }) => ReactNode;
  zIndex?: boolean;
  showAddFilter: boolean;
  isAiHistory?: boolean;
  getPopupContainer?: () => HTMLElement;
  /** Extra class on both filter popups, so a page (e.g. Home) can restyle
   *  them without changing the Shipments look. */
  popupClassName?: string;
  pillsDropdownMenuItems?: FilterMenuItems;
  isShipmentToolbarFilter?: boolean;
  savedFilterKey?: string;
}

export interface OptionItem {
  id: string;
  name?: string;
  title?: string;
  label?: string;
}

export interface RootState {
  ProductTypes?: {
    productTypeList?: unknown[];
  };
}
export type FilterConfigItem = {
  id: number;
  key: string;
  label: string;
};

export type FilterConfigGroup = {
  group: string;
  items: FilterConfigItem[];
};

export type FilterConfigShape = {
  topLevelFilters: FilterConfigItem[];
  groupedFilters: FilterConfigGroup[];
};

export type ConfigureFilterKeyFn = (filterKey: string) => void;
export type FilterSource = 'tab_base' | 'saved_preset' | 'manual';
export interface AppliedFilter {
  id: string;
  name?: string;
  title?: string;
  filterBy?: string;
  dateFilter?: string;
  logic?: 'AND' | 'OR';
  start?: string;
  end?: string;
  source?: FilterSource;
  [key: string]: unknown;
}
export interface FilterArrayItem {
  filterBy: string;
  filterValue: ID[] | string | number;
  logic?: 'AND' | 'OR';
  source?: FilterSource;
}

export interface SelectedOption {
  id: string;
  enabled?: boolean;
  name?: string;
  title?: string;
}
export interface FilterListsOverride {
  couriersList?: ListItem[];
  statesAndCities?: ListItem[];
  locationList?: ListItem[];
  shipmentStatusDDL?: ListItem[];
  healthStatusDDL?: ListItem[];
  deliveryStatusDDL?: ListItem[];
  productTypeList?: ListItem[];
  productList?: ListItem[];
  projectIdList?: ListItem[];
  allTeams?: ListItem[];
  shipmentLinkCodes?: ListItem[];
  savedFiltersSets?: ListItem[];
}

export interface UseFilterConfiguratorParams {
  setIsFilterOpen: (open: boolean) => void;
  setSecondaryFiltersDDL: (items: ListItem[]) => void;
  setSecondaryFiltersSource: (items: ListItem[]) => void;
  setIsDatePicker: (isDate: boolean) => void;
  setFilterTitle: (title: string) => void;
  setOptionsLoading?: (loading: boolean) => void;
  savedFilterKey?: string;
  lists?: FilterListsOverride; // optional overrides
}

export type PaginatedFetchFn<T> = (params: {
  page: number;
  limit: number;
  search?: string;
}) => Promise<{ data: T[]; pageCount: number }>;

export interface FilterLookups {
  couriersList: ListItem[];
  productTypeList: ListItem[];
  productList: ListItem[];
  projectIdList: ListItem[];
  allTeams: ListItem[];
  shipmentStatusDDL: ListItem[];
  shipmentStatusELDDL?: ListItem[];
  shipmentLinkCodes?: ListItem[];
  statesAndCities: ListItem[];
  PODStatusOptions?: ListItem[];
  locationTypeList: ListItem[];
  alertTypesDDL?: ListItem[];
  /** Sensor manufacturers (Tive, Controlant, …) for the Sensor filter. */
  sensorsList?: ListItem[];
  shipperRtgList?: ListItem[];
  consigneeRtgList?: ListItem[];

  fetchers?: {
    product_type?: PaginatedFetchFn<ListItem>;
    locations?: PaginatedFetchFn<ListItem>;
    grouped_locations?: PaginatedFetchFn<ListItem>;
    inventory_locations?: PaginatedFetchFn<ListItem>;
  };
}

interface ShipmentSummaryPayload {
  is_critical?: number | null;
  is_risk?: number | null;
  is_track?: number | null;
  version?: number;
  unchanged?: boolean;
}
interface ShipmentSummaryEnvelope {
  is_success: boolean;
  message?: string;
  data?: ShipmentSummaryPayload;
}
export interface SummaryApiResponse {
  data?: {
    data?: ShipmentSummaryEnvelope;
  };
}

export interface SecondaryFilterOption {
  id: string;
  name?: string;
  title?: string;
  enabled?: boolean;
  disabled?: boolean;
  courier_name?: string;
  label?: string;
  value?: string;
  name2?: string;
  filterCount?: number;
  tooltipText?: string;
  _isPreset?: boolean;
}

/** Applied filters coming from parent */
export interface AppliedFilter {
  id: string;
  name?: string;
  title?: string;
  filterBy?: string;
  dateFilter?: string;
  logic?: 'AND' | 'OR';
}

export interface APILocation {
  id?: string | number;
  location_id?: string | number;
  code?: string;
  name?: string;
  location_name?: string;
}

/** Stored location shape */
export interface StoredLocation {
  id: string | number;
  name?: string;
  title?: string;
}

/** Props for SubFilterMenu component */
export interface SubFilterMenuProps {
  loading?: boolean;
  hiddenOptionIds?: Set<string>;
  savedFilterKey?: string;
  dropdownMaxHeight?: number | string;
  filterBy: string;
  hideBackArrow?: boolean;
  alertTypesCount?: number;
  alertOptions?: ListItem[];
  onAlertTypesSearch?: (q: string) => void;
  secondaryFiltersDDL?: SecondaryFilterOption[];
  secondaryFiltersSource?: SecondaryFilterOption[];
  setSecondaryFiltersDDL?: (filters: SecondaryFilterOption[]) => void;
  setSecondaryFiltersSource?: (filters: SecondaryFilterOption[]) => void;

  onDeletePreset?: (presetId: string) => void;

  handleApplyPresetFilters?: (
    presetIds: { id: string | number }[],
    appendMode: boolean
  ) => void;

  appliedFilters?: AppliedFilter[];
  filterLogic?: 'AND' | 'OR';
  onFilterLogicChange?: (logic: 'AND' | 'OR') => void;

  selectedOptions: ListItem[];
  setSelectedOptions?: (options: ListItem[]) => void;

  filterTitle: string;

  handleAlertThresholdSelect?: (payload: {
    type: string;
    item?: SelectedOption;
  }) => void;

  handleSecondaryFilterSelect?: (
    e: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => void;

  handleApplyFilters?: () => void;
  onResetGroup?: () => void;

  setSecondFilterDisplay: (display: boolean) => void;
  setIsFilterOpen: (open: boolean) => void;

  initialItemsCount?: number;
  token?: string;

  /** Override internal paginated fetchers for specific keys.
   *  When provided for a key, the override fetcher is used instead of the
   *  one built from useFilterLookups. Use this to redirect pagination
   *  calls to a different API (e.g. getFilterControlFlowFields in filterControlFlow nodes). */
  fetcherOverrides?: {
    locations?: PaginatedFetcher<ListItem>;
    grouped_locations?: PaginatedFetcher<ListItem>;
    product_type?: PaginatedFetcher<ListItem>;
  };

  /** Override alert filter configuration for Logiblocks nodes.
   * When provided, AlertsBody will use this instead of fetching via useFetchAlertThresholds API. */
  alertFilterConfigOverride?: AlertSection[];

  storedFromLocation?: StoredLocation[];
  storedToLocation?: StoredLocation[];
}

/** Small constant used by alert counters */
export const AI_IDS = [
  'processing',
  'needs_attention',
  'resolved_via_ai',
] as const;
export type AIId = (typeof AI_IDS)[number];

export type PaginatedFetcher<Row> = (req: {
  page: number;
  limit: number;
  search?: string;
}) => Promise<{
  data: Row[];
  pageCount: number;
}>;

export type PaginatedConfig<Row> = {
  enabled: boolean;
  fetcher: PaginatedFetcher<Row>;
  limit: number;
  filterType?: string;
  mapToOption: (row: Row) => SecondaryFilterOption;
};
export interface CalendarTeam {
  id: string;
  name: string;
  color: string;
}

export interface CalendarShipmentItem {
  shipment_id: string | number;
  reference_number: string;
  leg_type?: 'CL' | 'EL';
  teams: CalendarTeam[];
  id?: string;
  type: number;
  status: string;
  alert_name: string;
  event_date: string;
  event_datetime?: string;
  event_end_date?: string;
  event_end_datetime?: string;
  shipment_type: string;
}

export interface CalendarHourAggregate {
  hour: string; // "YYYY-MM-DDTHH:00:00" in user timezone
  total: number;
  top_shipments: CalendarShipmentItem[];
}

export interface CalendarDayAggregate {
  date: string; // "YYYY-MM-DD"
  total: number;
  critical: number;
  at_risk: number;
  no_issue: number;
  top_shipments: CalendarShipmentItem[];
  hour_aggregates?: CalendarHourAggregate[]; // populated for week/day views
}

export type CalendarViewBy = 'created_at' | 'pickup_date' | 'arrival';

export type ApiSortOrder = 'ascending' | 'descending';

export interface CalendarShipmentsRequestBody {
  view_by: CalendarViewBy;
  leg_type?: 'CL' | 'EL';
  event_datetime?: string;
  page?: number;
  page_size?: number;
  sort_key?: string;
  sort_order?: ApiSortOrder;
  search?: string;
  search_query?: string;
  teams?: (string | number)[];
  service_levels?: (string | number)[];
}

export interface CalendarShipmentsEnvelope {
  is_success: boolean;
  message?: string;
  data?: CalendarDayAggregate[];
  total_shipment_count?: number;
  counts?: {
    critical: number;
    at_risk: number;
    no_issue: number;
  };
}

export interface MoreShipmentsPayload {
  shipments: CalendarMoreShipment[];
  total_pages: number;
}

export interface MoreShipmentsEnvelope {
  is_success: boolean;
  message?: string;
  data?: MoreShipmentsPayload;
}

export interface MoreShipmentsApiResponse {
  data: {
    data: MoreShipmentsEnvelope;
  };
}

export interface CalendarShipmentsApiResponse {
  data?: {
    data?: CalendarShipmentsEnvelope;
  };
}

export enum ShipmentAlertType {
  WARNING = 1, // yellow triangle
  CRITICAL = 2, // red exclamation
}

export enum ShipmentProgress {
  DELIVERED = '3',
  PENDING = '4',
  ARCHIVED = '5',
  IN_TRANSIT = '6',
  AWAITING_PICKUP = '7',
  DATA_LOADING = '8',
  DELIVERY_EXCEPTION = '9',
  NOT_FOUND = '10',
  NO_CARRIER_DATA = '11',
  TRACKING_ID_ISSUE = '12',
}

export type ShipmentLocation = {
  name?: string | null;
  address?: string | null;
};

export type CalendarMoreSortOrder = 'ascending' | 'descending';

export interface CalendarMoreTeam {
  id?: string;
  name?: string;
  color?: string;
}

export interface CalendarMoreLocation {
  id?: string;
  name?: string;
  address?: string;
  latlng?: unknown;
}

export interface CalendarMoreShipment {
  id: string;
  reference_number: string;
  status?: string | null;
  shipment_status?: string | null;

  type?: string | number | null;
  alert_count?: number | null;
  alert_status?: unknown[] | string | null;
  alert_type?: string | number | null;

  delivery_status?: string | null;

  estimated_delivery_date?: string | null;
  actual_delivery_date?: string | null;
  scheduled_delivery_date?: string | null;
  transport_provider_eta?: string | null;

  scheduled_pickup_date?: string | null;
  actual_pickup_date?: string | null;

  from_location?: CalendarMoreLocation | null;
  to_location?: CalendarMoreLocation | null;

  teams?: CalendarMoreTeam[];

  created_at?: string | null;
  updated_at?: string | null;

  // keep index signature so TS won’t fight you as backend adds fields
  [key: string]: unknown;
}

export interface CalendarMoreShipmentsEnvelope {
  page: number;
  page_size: number;

  shipments: CalendarMoreShipment[];

  sort_key?: string;
  sort_order?: CalendarMoreSortOrder;

  total_count?: number;
  total_pages?: number;

  is_success?: boolean;
  message?: string;
}

export interface CalendarMoreShipmentsApiResponse {
  data?: {
    data?: CalendarMoreShipmentsEnvelope;
  };
}

export interface GroupedLocationSingle {
  type: 'single';
  id: string;
  name: string;
  address?: string;
  children_count: number;
  has_children: boolean;
}

export interface GroupedLocationChild {
  id: string;
  name: string;
  address?: string;
}

export interface GroupedLocationGroup {
  type: 'group';
  id: string;
  display_name: string;
  address?: string;
  children_count: number;
  has_children: boolean;
  locations: GroupedLocationChild[];
}

export type GroupedLocationItem = GroupedLocationSingle | GroupedLocationGroup;

export interface GroupedLocationsV2Response {
  items: GroupedLocationItem[];
  pagination: {
    page: number;
    limit: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export type ApiEnvelope<T> = {
  messageTypeId: string;
  message?: string;
  data: {
    data: T;
  };
};

export type LocationSelectedOption = {
  id: string;
  name: string;
};

export type LocationRow =
  | {
      type: 'single';
      id: string;
      name: string;
      address?: string;
      children_count: number;
      has_children: boolean;
    }
  | {
      type: 'group';
      id: string;
      name: string;
      display_name: string;
      address?: string;
      children_count: number;
      has_children: boolean;
      locations: GroupedLocationChild[];
    };

// ---------------------------------------------------------------------------
// Shipment Tab types
// ---------------------------------------------------------------------------

export type FilterSetVisibility = 'personal' | 'team' | 'public';
export type ShipmentLegType = 'CL' | 'EL';

export interface SavedFilterSet {
  id: string;
  name?: string;
  visibility: FilterSetVisibility;
  legType?: ShipmentLegType;
  /** Base filter criteria — same shape as FilterArrayItem[] stored as backend "filters" */
  filters: FilterArrayItem[];
  /** Applied filter objects (legacy "presets" field in backend) */
  appliedFilters: AppliedFilter[];
  teamIds: string[];
  teamsDisplay: { id: string; name: string }[];
  isOwner: boolean;
  filterCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  is_default?: boolean;
  saved_filter_set?: {
    name?: string;
  };
}

export interface ShipmentTab {
  id: string;
  order: number;
  savedFilterSet: SavedFilterSet;
  additionalFilters: AppliedFilter[];
  sort_key?: string;
  sort_order?: 'ascending' | 'descending';
  createdBy?: string;
}

export type DefaultTabId = 'all_shipments' | 'active';

export interface DefaultTab {
  id: DefaultTabId;
  label: string;
  isDefault: true;
  sort_key?: string;
  sort_order?: 'ascending' | 'descending';
}

export type AnyTab = DefaultTab | ShipmentTab;

export interface AvailableFilterSetsResponse {
  shared: SavedFilterSet[];
  personal: SavedFilterSet[];
}

export interface TabsResponse {
  defaultTabs: DefaultTab[];
  customTabs: ShipmentTab[];
}

export interface CreateSavedFilterSetBody {
  name: string;
  visibility: FilterSetVisibility;
  filters: FilterArrayItem[];
  legType: ShipmentLegType;
  appliedFilters?: AppliedFilter[];
  teamIds?: string[];
  pin_to_tab?: boolean;
}

export interface UpdateTabBody {
  order?: number;
  additionalFilters?: AppliedFilter[];
}

export type ShipmentActivityBell = 'on' | 'muted' | null;

export interface ShipmentActivityPreviewItem {
  id?: string | number | null;
  author?: string | null;
  author_name?: string | null;
  author_initials?: string | null;
  audience?: string | null;
  audience_display?: string | null;
  timestamp?: string | null;
  text?: string | null;
  mentions_me?: boolean | null;
}

export interface ShipmentActivity {
  comment_count: number;
  has_unread: boolean;
  has_unread_mention: boolean;
  unread_count: number;
  mention_unread_count: number;
  preview: ShipmentActivityPreviewItem[];
  bell: ShipmentActivityBell;
}

export interface ShipmentStatusStage {
  key?: string;
  is_completed?: boolean;
}

export interface ShipmentCustomStatus {
  current_stage?: string;
  stages?: ShipmentStatusStage[];
}

export interface ShipmentEtas {
  estimated_delivery_date?: string | null;
  actual_pickup_date?: string | null;
  scheduled_pickup_date?: string | null;
  actual_delivery_date?: string | null;
  scheduled_delivery_date?: string | null;
}

export type ShipmentCellValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ShipmentActivity
  | Record<string, unknown>
  | unknown[];

export interface ShipmentHeader {
  key: string;
  value: string;
  enable?: boolean;
  group_name?: string;
  isCustom?: boolean;
}

export interface ShipmentRow {
  id?: string;
  piece_count?: number;
  shipment_id?: string;
  reference_number?: string;
  leg_type?: 'CL' | 'EL';
  tracking_num?: string;
  link_code?: string;

  status?: string;
  progress?: string;
  updates?: string;
  custom_status?: ShipmentCustomStatus;
  etas?: ShipmentEtas;

  origin_name?: string;
  origin_address?: string;

  consignee_name?: string;
  consignee_address?: string;

  product_name?: string;
  courier_name?: string;
  created_at?: string;
  teams?: {
    color: string;
    name?: string;
  };
  from_location?: {
    name?: string;
    parsed_address?: {
      StateName?: string;
    };
  };

  to_location?: {
    name?: string;
    parsed_address?: {
      PlaceName?: string;
    };
  };

  products?: Array<{
    name?: string;
  }>;

  activity?: ShipmentActivity;
  alert_activity?: ShipmentAlertActivitySummary;

  /** Per-shipment permission matrix returned by the API -- used to gate
   *  record-specific actions (e.g. Edit) instead of the global menu-level
   *  shipment permissions. */
  permissions?: FeaturePermissionMatrix;

  /** Per-row flag: gates "Mark as delivered" and "Archive shipment". */
  can_manage_record?: boolean;
  /** Per-row flag: gates "Dismiss alerts". */
  can_dismiss_alerts?: boolean;

  /**
   * Most-recent-per-alert-type excursion value/sensor, from ShipmentAlert.
   * `current_value`/`current_unit` prefer the raw `trigger` JSON (numeric)
   * over the flat model columns (string) -- the flat columns are rarely
   * populated, so either shape can come through.
   */
  alert_details?: Array<{
    alert_type: string;
    title: string;
    current_value: string | number | null;
    current_unit: string | null;
    shipment_sensor_id: string | null;
    /** shipment_sensor_id resolved to its sensor_tracking_id (physical
     *  code) -- matches the per-vital sensor-reading blob's sensor_id,
     *  unlike shipment_sensor_id (a ShipmentSensor UUID). */
    sensor_tracking_id: string | null;
    occurred_at: string | null;
  }>;

  /**
   * Per-vital "Product requirements" thresholds, always sent regardless of
   * whether that column group is toggled on -- used to color individual
   * sensor readings against their range. temperature/humidity/pressure
   * have a real min and max; shock/light only have a max (single upper
   * bound). Battery has no threshold field anywhere.
   */
  temperature_min?: number | string | null;
  temperature_max?: number | string | null;
  humidity_min?: number | string | null;
  humidity_max?: number | string | null;
  pressure_min?: number | string | null;
  pressure_max?: number | string | null;
  shock?: number | string | null;
  light?: number | string | null;

  [key: string]: unknown;
}

export type CustomerExecutionView = 'cl' | 'el';

export type CustomerExecutionLocation = {
  [key: string]: unknown;
  id?: string;
  name?: string;
  address?: string;
  latlng?: {
    [key: string]: unknown;
    lat?: number;
    lng?: number;
  };
  parsed_address?: {
    [key: string]: unknown;
    StateName?: string;
    PlaceName?: string;
  };
};

export type CustomerExecutionTransportProvider = {
  [key: string]: unknown;
  id?: string;
  title?: string;
  name?: string;
  tracking_id?: string;
  tracking_link?: string;
};

export type CustomerExecutionLegMode = {
  [key: string]: unknown;
  value?: string;
  name?: string;
  icon_token?: string;
};

export type CustomerExecutionEtas = {
  [key: string]: unknown;
  scheduled_pickup_date?: string | null;
  actual_pickup_date?: string | null;
  scheduled_delivery_date?: string | null;
  actual_delivery_date?: string | null;
  transport_provider_eta?: string | null;
  estimated_delivery_date?: string | null;
  shipment_date?: string | null;
};

export type CustomerExecutionLegApiRow = {
  [key: string]: ShipmentCellValue;
  id?: string;
  reference_number?: string;
  tracking?: ShipmentCellValue;
  custom_status?: ShipmentCellValue;
  origin?: CustomerExecutionLocation;
  destination?: CustomerExecutionLocation;
  from_location?: CustomerExecutionLocation;
  to_location?: CustomerExecutionLocation;
  transport_provider?: CustomerExecutionTransportProvider;
  etas?: CustomerExecutionEtas;
  leg_mode?: CustomerExecutionLegMode | string;
  mode?: string;
  courier_name?: string;
  courier_tracking_id?: string;
  scheduled_pickup_date?: string | null;
  actual_pickup_date?: string | null;
  scheduled_delivery_date?: string | null;
  actual_delivery_date?: string | null;
  transport_provider_eta?: string | null;
  estimated_delivery_date?: string | null;
  shipment_date?: string | null;
};

export interface ShipmentReportData {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  header: ShipmentHeader[];
  data: ShipmentRow[];
}

export interface ShipmentReportResponse {
  data: {
    message: {
      text: string;
      title: string;
      messageTypeId: string;
    };
    data: {
      message: string;
      status: number;
      is_success: boolean;
      messageTypeId?: string;
      page_count?: number;
      data: ShipmentReportData;
    };
  };
}

export interface CompanionHandoffIntent {
  kind: 'default' | 'shared' | 'personal';
  id: string;
  sortKey?: string;
  sortOrder?: 'ascending' | 'descending';
}

export interface UseCompanionShipmentsHandoffParams {
  token: string;
  customTabs: ShipmentTab[];
  tabsLoaded: boolean;
}

export interface UseCompanionShipmentsHandoffResult {
  isResolving: boolean;
  resolvedTabId: string | null;
  resolvedSortKey?: string;
  resolvedSortOrder?: 'ascending' | 'descending';
  /** Set once, when a shared/personal view had to be pinned as a new tab —
   *  the caller should add it to its own tabs list. */
  newlyPinnedTab: ShipmentTab | null;
}
